import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline } from "@mdi/js"
import { normalizeCountryLabel } from "../../../../../../../_shared/_logic/countries"
import {
  buildPropertyObjectTagPresentation,
  type PropertyObjectTagPresentation,
} from "../../../../../_shared/_logic/object-tags"
import {
  buildAppointmentSubLabel,
  resolveAppointmentContact,
  type AppointmentContactPresentation,
} from "../../../../../../../_shared/_logic/appointment-contact"
import {
  buildAppointmentTimeTagPresentation,
  type AppointmentTimeTagInput,
} from "../../../../../../../_shared/_logic/appointment-time-tags"
import {
  buildScheduleEventCommunicationTagPresentation,
  type ScheduleEventCommunicationPresentationInput,
} from "../../../../../../../_shared/_logic/communication-presentation"
import { type WorkOrderViewStatus } from "../../../../../../../_shared/_logic/outreach-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../_shared/_logic/rich-tooltips"
import {
  buildAppointmentHistoryItems,
  type WorkOrderAppointmentHistoryItem,
} from "../../../../../_shared/_logic/appointment-history"
import { workOrderStatusByCode } from "../status-tags"
import {
  buildWorkOrderSummaryTagPresentation,
  type WorkOrderSummaryTagPresentation,
} from "../../../../../_shared/_logic/summary-tag"

export type WorkOrderAppointmentTagPresentation = {
  status: StatusDefinition<string> | null
  subLabel?: string | null
  tooltipContent?: string | null
  historyItems?: WorkOrderAppointmentHistoryItem[]
  fallbackLabel?: string | null
}

export type WorkOrderPropertyContextTagPresentation = {
  tag: PropertyObjectTagPresentation
  tooltipContent: string
}

export type WorkOrderContextTagsPresentation = {
  appointmentTags: WorkOrderAppointmentTagPresentation[]
  workOrderSummaryTag: WorkOrderSummaryTagPresentation | null
  propertyTag: WorkOrderPropertyContextTagPresentation | null
}

type AppointmentReference = AppointmentTimeTagInput & {
  id?: string | null
  schedule_event_id?: string | null
  role?: string | null
  status?: ScheduleEventStatus | string | null
  employee_name?: string | null
  communication_presentation?: ScheduleEventCommunicationPresentationInput | null
}

type WorkOrderStatusSource = WorkOrder & {
  view_status?: WorkOrderViewStatus | null
  close_reason?: string | null
  close_description?: string | null
}

export const withWorkOrderDuration = <T extends AppointmentTimeTagInput>(
  event: T | null | undefined,
  workOrder?: WorkOrder | null,
): (T & AppointmentTimeTagInput) | null => {
  if (!event) return null

  return {
    ...event,
    estimated_duration_min:
      event.estimated_duration_min ??
      (event as { duration_minutes?: number | string | null }).duration_minutes ??
      workOrder?.estimated_duration_min ??
      null,
  }
}

export const formatActiveAppointment = (
  event?: ScheduleEvent | null,
  workOrder?: WorkOrder | null,
) => {
  if (!event) return "Kein Termin"

  return buildAppointmentTimeTagPresentation(withWorkOrderDuration(event, workOrder)).label
}

export const formatWorkOrderAppointmentLabel = ({
  event,
  workOrder,
  statusCode,
}: {
  event?: ScheduleEvent | null
  workOrder?: WorkOrder | null
  statusCode: WorkOrderViewStatus | null
}) => {
  if (event) return formatActiveAppointment(event, workOrder)

  return statusCode === "COMPLETED" ? "Terminlos erledigt" : "Kein Termin"
}

export const buildAppointmentStatusDefinition = ({
  statusCode,
  appointmentLabel,
  appointmentStatus,
}: {
  statusCode: WorkOrderViewStatus | null
  appointmentLabel: string
  appointmentStatus?: ScheduleEventStatus | string | null
}): StatusDefinition<string> | null => {
  if (appointmentStatus) {
    const presentation = buildScheduleEventCommunicationTagPresentation({
      status: appointmentStatus,
    })

    return { ...presentation.definition, label: appointmentLabel }
  }

  const base = statusCode ? workOrderStatusByCode[statusCode] : null

  return base ? { ...base, color: "neutral", label: appointmentLabel || "Kein Termin" } : null
}

export const buildStatusDescription = ({
  order,
  statusCode,
  relevantEvent,
  hasActiveContactOrder,
  appointmentContact,
  communicationDetail = null,
}: {
  order: WorkOrder
  statusCode: WorkOrderViewStatus | null
  relevantEvent?: ScheduleEvent | null
  hasActiveContactOrder: boolean
  employeeLabel?: string | null
  appointmentContact?: AppointmentContactPresentation | null
  communicationDetail?: string | null
}) => {
  const appointmentTime = buildAppointmentTimeTagPresentation(
    withWorkOrderDuration(relevantEvent, order),
  )
  const orderStatusSource = order as WorkOrderStatusSource
  const explanation = (() => {
    if (statusCode === "FAILED")
      return (
        orderStatusSource.close_description ||
        "Der Auftrag wurde ohne erfolgreiche Durchführung beendet."
      )

    if (statusCode === "COMPLETED")
      return relevantEvent
        ? "Die Durchführung wurde dokumentiert."
        : "Der Auftrag wurde ohne Termin als erledigt markiert."

    if (communicationDetail) return communicationDetail

    if (relevantEvent?.status === "CONFIRMED") return "Der Termin wurde verbindlich bestätigt."

    if (relevantEvent?.status === "ANNOUNCED") {
      return "Der Kunde wurde über den Termin informiert, hat ihn aber noch nicht bestätigt."
    }

    if (!relevantEvent) return "Zuerst einen Termin planen."

    return hasActiveContactOrder
      ? "Die Ankündigung ist noch nicht abgeschlossen."
      : "Festlegen, wie der Kunde über den Termin informiert werden soll."
  })()

  return buildRichTooltip([
    tooltipText(explanation),
    appointmentTime.usesCustomerWindow
      ? tooltipField("Kalenderzeit", appointmentTime.scheduledPeriodLabel)
      : null,
    appointmentTime.tooltipDescription ? tooltipText(appointmentTime.tooltipDescription) : null,
    tooltipField("Bestätigt durch", appointmentContact?.confirmedByLabel),
  ])
}

export const formatAddressTooltip = (property?: Property | null) => {
  if (!property) return buildRichTooltip([tooltipText("Unbekannte Liegenschaft")])

  return buildRichTooltip([
    tooltipField("Postleitzahl", property.zip),
    tooltipField("Adresszusatz", property.address_extra),
    normalizeCountryLabel(property.country) !== "Deutschland"
      ? tooltipField("Land", normalizeCountryLabel(property.country))
      : null,
  ])
}

export const buildWorkOrderAppointmentContextTag = ({
  workOrder,
  relevantEvent,
  scheduleEvents = [],
  hasActiveContactOrder = false,
  employeeLabel = null,
  appointmentContact = null,
  customersById = {},
  contactPoints = [],
}: {
  workOrder?: WorkOrder | null
  relevantEvent?: ScheduleEvent | null
  scheduleEvents?: ScheduleEvent[]
  hasActiveContactOrder?: boolean
  employeeLabel?: string | null
  appointmentContact?: AppointmentContactPresentation | null
  customersById?: Record<string, Customer | undefined>
  contactPoints?: ContactPoint[]
}): WorkOrderAppointmentTagPresentation => {
  const statusCode = ((workOrder as WorkOrderStatusSource | null)?.view_status ??
    null) as WorkOrderViewStatus | null
  const appointmentLabel = formatWorkOrderAppointmentLabel({
    event: relevantEvent,
    workOrder,
    statusCode,
  })
  const resolvedContact =
    appointmentContact ??
    resolveAppointmentContact({
      event: relevantEvent,
      workOrder,
      customersById,
      contactPoints,
    })
  const resolvedEmployeeLabel =
    employeeLabel ??
    (relevantEvent as { employee_name?: string | null } | null)?.employee_name ??
    null
  const tooltip = workOrder
    ? buildStatusDescription({
        order: workOrder,
        statusCode,
        relevantEvent,
        hasActiveContactOrder,
        employeeLabel: resolvedEmployeeLabel,
        appointmentContact: resolvedContact,
      })
    : null

  const communication = relevantEvent
    ? buildScheduleEventCommunicationTagPresentation({
        status: relevantEvent.status,
        event: relevantEvent,
      })
    : null

  return {
    status: communication
      ? { ...communication.definition, label: appointmentLabel }
      : buildAppointmentStatusDefinition({
          statusCode,
          appointmentLabel,
          appointmentStatus: relevantEvent?.status ?? null,
        }),
    subLabel:
      appointmentLabel !== "Kein Termin"
        ? [
            communication?.label,
            buildAppointmentSubLabel({
              employeeLabel: resolvedEmployeeLabel,
              prefixEmployeeOnly: true,
            }),
          ]
            .filter(Boolean)
            .join(" · ")
        : null,
    tooltipContent: communication?.tooltip || tooltip,
    historyItems: workOrder
      ? buildAppointmentHistoryItems({
          workOrderId: workOrder.id,
          events: scheduleEvents,
          currentEventId: relevantEvent?.id ?? null,
        })
      : [],
    fallbackLabel: appointmentLabel,
  }
}

const fallbackAppointmentStatus = (value: string, label: string): StatusDefinition<string> => ({
  value,
  label,
  icon: mdiCalendarClockOutline,
  color: "blue",
})

const formatAppointmentEvidenceTime = (value: string): string => {
  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return value

  return parsed.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  })
}

const communicationAppointmentRefTag = (
  ref: AppointmentReference,
  workOrder?: WorkOrder | null,
): WorkOrderAppointmentTagPresentation => {
  const timeTag = buildAppointmentTimeTagPresentation(withWorkOrderDuration(ref, workOrder))
  const communicationPresentation = buildScheduleEventCommunicationTagPresentation({
    status: ref.status ?? "RESERVED",
    presentation: ref.communication_presentation,
    event: ref as ScheduleEvent,
  })
  const statusDefinition = { ...communicationPresentation.definition, label: timeTag.label }
  const employeeSubLabel = buildAppointmentSubLabel({
    employeeLabel: ref.employee_name,
    prefixEmployeeOnly: true,
  })

  return {
    status: statusDefinition,
    subLabel:
      [communicationPresentation.label, employeeSubLabel]
        .filter((value): value is string => Boolean(value))
        .join(" · ") || null,
    tooltipContent:
      communicationPresentation.tooltip +
      buildRichTooltip([
        timeTag.usesCustomerWindow
          ? tooltipField("Kalenderzeit", timeTag.scheduledPeriodLabel)
          : null,
        timeTag.tooltipDescription ? tooltipText(timeTag.tooltipDescription) : null,
      ]),
    historyItems: [],
    fallbackLabel: timeTag.label,
  }
}

const scheduleEventReferenceTag = (
  event: ScheduleEvent,
  workOrder?: WorkOrder | null,
): WorkOrderAppointmentTagPresentation =>
  communicationAppointmentRefTag(event as AppointmentReference, workOrder)

const communicationScheduleEventTag = ({
  event,
  workOrder = null,
}: {
  event: ScheduleEvent
  workOrder?: WorkOrder | null
  scheduleEvents?: ScheduleEvent[]
}): WorkOrderAppointmentTagPresentation => {
  const tag = scheduleEventReferenceTag(event, workOrder)

  if (!workOrder?.id) return tag

  return {
    ...tag,
    tooltipContent: tag.tooltipContent ?? "",
  }
}

export const buildCommunicationAppointmentContextTags = ({
  appointmentRefs = [],
  scheduleEvent = null,
  workOrder = null,
  scheduleEvents = [],
}: {
  appointmentRefs?: AppointmentReference[] | null
  scheduleEvent?: ScheduleEvent | null
  workOrder?: WorkOrder | null
  scheduleEvents?: ScheduleEvent[]
  customersById?: Record<string, Customer | undefined>
  contactPoints?: ContactPoint[]
}): WorkOrderAppointmentTagPresentation[] => {
  const refs = (appointmentRefs ?? []).filter((ref) => ref?.schedule_event_id || ref?.id)

  if (refs.length)
    return refs.slice(0, 2).map((ref) => communicationAppointmentRefTag(ref, workOrder))

  if (scheduleEvent)
    return [communicationScheduleEventTag({ event: scheduleEvent, workOrder, scheduleEvents })]

  return [
    {
      status: {
        value: "NO_APPOINTMENT_REF",
        label: "Kein Terminbezug",
        icon: mdiCalendarClockOutline,
        color: "slate",
      },
      subLabel: null,
      tooltipContent: buildRichTooltip([
        tooltipTitle("Termin"),
        tooltipText("Diese Kommunikation ist mit keinem konkreten Termin verknüpft."),
      ]),
      historyItems: [],
    },
  ]
}

export const buildWorkOrderSummaryContextTag = (
  workOrder?: WorkOrder | null,
): WorkOrderSummaryTagPresentation | null => {
  if (!workOrder) return null

  return buildWorkOrderSummaryTagPresentation({ workOrder })
}

export const buildWorkOrderPropertyContextTag = ({
  property,
  workOrder,
}: {
  property?: Property | null
  workOrder?: WorkOrder | null
}): WorkOrderPropertyContextTagPresentation | null => {
  if (!property && !workOrder?.property_id) return null

  const fallbackUnitLabel =
    (workOrder as { property_unit_label?: string | null } | null)?.property_unit_label ?? null
  const tag = buildPropertyObjectTagPresentation({
    property,
    propertyUnitId: workOrder?.property_unit_id,
    fallbackUnitLabel,
    mode: "address-first",
  })

  return {
    tag,
    tooltipContent: formatAddressTooltip(property),
  }
}
